import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  BulkAction,
  BulkBar,
  ListScreen,
  Notice,
  TableSkeleton,
} from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { ConfirmDialog, Dialog, Drawer } from '../components/ui/dialog'
import { Field, Input } from '../components/ui/field'
import { Blank, Panel } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip, type Tone } from '../components/ui/tone'
import {
  ApiError,
  api,
  eventDetail,
  eventTransition,
  type AddContactsResult,
  type ApprovalStatus,
  type Campaign,
  type CampaignContactStatus,
  type CampaignDetail,
  type CampaignRecipientPage,
  type CampaignState,
  type ContactPage,
  type Page,
  type PlanResult,
  type Readiness,
  type ReviewResult,
} from '../lib/api'
import { RecipientPreviewDrawer } from '../components/campaigns/recipient-preview'
import { StepsPanel } from '../components/campaigns/steps-panel'
import { useSession } from '../lib/session'
import { cn } from '../lib/utils'

/**
 * Campaigns (§45, §81, §99).
 *
 * The screen where the single most consequential control in the product lives: leaving dry run.
 * Everything else here can be undone; that one turns a rehearsal into real email to real academics.
 *
 * So it is treated the way the kill switch is treated on Overview — the destructive direction is
 * harder to press than the safe one. Entering dry run is a single click. Leaving it takes a typed
 * reason, which the API also requires, so pasting a request past this dialog gains nothing.
 */

const STATE_LABEL: Record<CampaignState, string> = {
  DRAFT: 'Draft',
  VALIDATING: 'Validating',
  AWAITING_APPROVAL: 'Awaiting approval',
  READY: 'Ready',
  RUNNING: 'Running',
  PAUSING: 'Pausing',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  ARCHIVED: 'Archived',
  FAILED: 'Failed',
}

/** Colour only where something needs attention. Draft and Completed are unremarkable. */
const STATE_TONE: Record<CampaignState, Tone> = {
  DRAFT: 'neutral',
  VALIDATING: 'neutral',
  AWAITING_APPROVAL: 'warn',
  READY: 'info',
  RUNNING: 'ok',
  PAUSING: 'warn',
  PAUSED: 'warn',
  COMPLETED: 'neutral',
  CANCELLED: 'neutral',
  ARCHIVED: 'neutral',
  FAILED: 'bad',
}

/**
 * §64's four answers. `NOT_REQUIRED` is not an approval — it is a campaign that turned review off,
 * and it must not read as "somebody approved this".
 */
const APPROVAL_LABEL: Record<ApprovalStatus, string> = {
  NOT_REQUIRED: 'Review off',
  PENDING: 'Awaiting review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
}

const APPROVAL_TONE: Record<ApprovalStatus, Tone> = {
  NOT_REQUIRED: 'neutral',
  PENDING: 'warn',
  APPROVED: 'ok',
  REJECTED: 'bad',
}

/** Where one recipient has got to (§45), as opposed to what a person decided about them. */
const RECIPIENT_STATE_LABEL: Record<CampaignContactStatus, string> = {
  PENDING: 'not scheduled',
  QUALIFYING: 'qualifying',
  AWAITING_APPROVAL: 'awaiting approval',
  APPROVED: 'not scheduled',
  REJECTED: 'rejected',
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'sending',
  REPLIED: 'replied',
  COMPLETED: 'completed',
  STOPPED: 'stopped',
  SUPPRESSED: 'suppressed',
}

export function CampaignsScreen({ onCrumb }: { onCrumb?: (crumb?: string) => void }) {
  const { can } = useSession()
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<Page<Campaign>>('/campaigns?take=200'),
    enabled: can('view_contacts'),
  })

  if (!can('view_contacts')) {
    return (
      <Screen>
        <ScreenHeader title="Campaigns" description="Outreach programmes and their state." />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to campaigns"
            body="Ask an administrator of your organization."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = (campaigns.data?.items ?? []).filter(
    (campaign) => !search || campaign.name.toLowerCase().includes(search.toLowerCase()),
  )

  const live = rows.filter((c) => !c.dryRun).length

  return (
    <>
      <ListScreen
        title="Campaigns"
        subtitle="Every campaign starts in dry run and stays there until someone deliberately takes it out, with a reason (§99)."
        query={search}
        onQuery={setSearch}
        queryPlaceholder="Filter campaigns"
        primary={
          can('manage_campaigns') ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              New campaign
            </Button>
          ) : undefined
        }
        notice={
          live > 0 ? (
            <Notice tone="warn">
              {live === 1 ? '1 campaign is' : `${live} campaigns are`} out of dry run. Once running,
              {live === 1 ? ' it' : ' they'} send real mail to real people.
            </Notice>
          ) : undefined
        }
      >
        {campaigns.isLoading ? (
          <TableSkeleton columns={6} />
        ) : campaigns.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="Campaigns could not be loaded"
            action={<Button onClick={() => void campaigns.refetch()}>Try again</Button>}
          />
        ) : rows.length > 0 ? (
          <Table>
            <THead>
              <Th>Name</Th>
              <Th width={150}>State</Th>
              <Th width={110}>Mode</Th>
              <Th width={110} align="right">
                Recipients
              </Th>
              <Th width={110} align="right">
                Sent
              </Th>
              <Th width={120}>Created</Th>
            </THead>
            <TBody>
              {rows.map((campaign) => (
                <Tr
                  key={campaign.id}
                  onOpen={() => {
                    setOpenId(campaign.id)
                    onCrumb?.(campaign.name)
                  }}
                >
                  <Td>{campaign.name}</Td>
                  <Td>
                    <Chip tone={STATE_TONE[campaign.state]}>{STATE_LABEL[campaign.state]}</Chip>
                  </Td>
                  <Td>
                    {/* The one column an operator scans for. A live campaign is the exception and
                        has to look like one. */}
                    {campaign.dryRun ? (
                      <span className="text-muted">dry run</span>
                    ) : (
                      <Chip tone="bad">live</Chip>
                    )}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {campaign._count.campaignContacts.toLocaleString()}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {campaign._count.outboundMessages.toLocaleString()}
                  </Td>
                  <Td className="text-muted">
                    {new Date(campaign.createdAt).toLocaleDateString()}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : search ? (
          <Blank
            icon="noResults"
            title={`No campaign matches “${search}”.`}
            action={<Button onClick={() => setSearch('')}>Clear filter</Button>}
          />
        ) : (
          <Blank
            icon="campaigns"
            title="No campaigns yet."
            body="A campaign is a sending programme: a list of contacts, a template, and the schedule it goes out on. Nothing can actually send until Phase 3 — a campaign created now is a rehearsal."
            action={
              can('manage_campaigns') ? (
                <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                  New campaign
                </Button>
              ) : undefined
            }
          />
        )}
      </ListScreen>

      <CreateDialog open={creating} onClose={() => setCreating(false)} />

      <Drawer
        open={openId !== null}
        title="Campaign"
        width={640}
        onClose={() => {
          setOpenId(null)
          onCrumb?.(undefined)
        }}
      >
        {openId && <CampaignDetailPanel id={openId} />}
      </Drawer>
    </>
  )
}

function CampaignDetailPanel({ id }: { id: string }) {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const campaign = useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => api.get<CampaignDetail>(`/campaigns/${id}`),
  })

  const readiness = useQuery({
    queryKey: ['campaigns', id, 'readiness'],
    queryFn: () => api.get<Readiness>(`/campaigns/${id}/readiness`),
  })

  const dryRun = useMutation({
    mutationFn: ({ value, reason }: { value: boolean; reason?: string }) =>
      api.post(`/campaigns/${id}/dry-run`, { dryRun: value, reason }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The change was not applied'),
  })

  if (campaign.isLoading) return <p className="text-faint p-4 text-[13px]">Loading…</p>
  if (!campaign.data)
    return <p className="text-faint p-4 text-[13px]">This campaign no longer exists.</p>

  const c = campaign.data

  return (
    // The drawer's own body carries no padding — it cannot, because a drawer holding a table needs
    // to bleed to its edges. Every screen that opens one pads its own content, and this one did
    // not, so the campaign panel sat flush against the border.
    <div className="space-y-6 p-4 text-[13px]">
      <div>
        <h3 className="text-text m-0 text-[15px] font-semibold">{c.name}</h3>
        <div className="mt-2 flex items-center gap-2">
          <Chip tone={STATE_TONE[c.state]}>{STATE_LABEL[c.state]}</Chip>
          {c.dryRun ? <Chip>dry run</Chip> : <Chip tone="bad">live</Chip>}
        </div>
      </div>

      {c.pausedReason && (
        <Notice tone="warn">Paused: {c.pausedReason}</Notice>
      )}

      <dl className="grid grid-cols-[150px_1fr] gap-y-2.5">
        <dt className="text-muted">Recipients</dt>
        <dd className="tabular-nums">{c._count.campaignContacts.toLocaleString()}</dd>
        <dt className="text-muted">Messages sent</dt>
        <dd className="tabular-nums">{c._count.outboundMessages.toLocaleString()}</dd>
        <dt className="text-muted">Daily limit</dt>
        <dd>{c.dailyLimit === null ? 'inherited' : c.dailyLimit.toLocaleString()}</dd>
        <dt className="text-muted">Human approval</dt>
        <dd>{c.requiresApproval ? 'required for every message' : 'not required'}</dd>
        <dt className="text-muted">Journal</dt>
        <dd>{c.journal ? c.journal.name : <span className="text-faint">none assigned</span>}</dd>
      </dl>

      {/* §81. What would stop it, named — a disabled button with no reason is the thing an
          operator works around instead of fixing. */}
      {readiness.data && (
        <section>
          <h4 className="text-text m-0 mb-2 text-[13px] font-semibold">Before it can send</h4>
          {readiness.data.blocking.length === 0 ? (
            <p className="text-muted">Nothing recorded here is blocking it.</p>
          ) : (
            <ul className="text-muted m-0 list-none space-y-1.5 p-0">
              {readiness.data.blocking.map((reason) => (
                <li key={reason}>· {reason}</li>
              ))}
            </ul>
          )}
          <p className="text-faint mt-3 text-[12.5px]">
            Not yet checked at all: {readiness.data.pending.join('; ')}.
          </p>
        </section>
      )}

      <StepsPanel campaignId={c.id} />

      <Recipients campaign={c} />

      {can('run_dry_run') && (
        <div className="border-line flex items-center gap-2 border-t pt-4">
          {c.dryRun ? (
            <Button
              variant="danger"
              disabled={dryRun.isPending}
              onClick={() =>
                dryRun.mutate({
                  value: false,
                  // §99 requires a written reason and the console supplies one. See the note by
                  // the kill switch on the Overview screen for why it stopped asking.
                  reason: 'Taken out of dry run from the console.',
                })
              }
            >
              {dryRun.isPending ? 'Going live…' : 'Take out of dry run'}
            </Button>
          ) : (
            <Button
              onClick={() => dryRun.mutate({ value: true })}
              disabled={dryRun.isPending}
            >
              Put back into dry run
            </Button>
          )}
          {error && (
            <span role="alert" className="text-bad text-[12.5px]">
              {error}
            </span>
          )}
          <span className="text-faint text-[12.5px]">
            {c.dryRun
              ? 'While in dry run the pipeline runs and stops before the provider call.'
              : 'This campaign can send real mail once it is running.'}
          </span>
        </div>
      )}

      <section>
        <h4 className="text-text m-0 mb-2 text-[13px] font-semibold">History</h4>
        {c.events.length === 0 ? (
          <p className="text-faint">Nothing has happened to this campaign yet.</p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {c.events.map((event) => {
              // The reason, where the event carries one. Until P046 every refusal was written
              // with its explanation in the payload and the screen printed only the type, so an
              // operator saw MESSAGE_REFUSED five times and had to read the server log to find
              // out what for.
              const detail = eventDetail(event.payload)
              // §6's EventType has one value for every transition, so a campaign that was started
              // is recorded as CAMPAIGN_PAUSED. The from/to pair in the payload is what actually
              // happened; printing the type alone made this list say the opposite of the truth
              // (P062). Where a transition is present it replaces the type rather than joining it.
              const transition = eventTransition(event.payload)
              return (
                <li key={event.id} className="text-muted">
                  <span className="text-faint tabular-nums">
                    {new Date(event.createdAt).toLocaleString()}
                  </span>{' '}
                  — {transition ?? event.type}
                  {detail && <span className="text-faint"> · {detail}</span>}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/*
        One press, both directions — owner instruction, 2026-08-27.

        §57 still gets a reason; the console writes it rather than asking a person to type one. The
        friction was buying nothing real: a required sentence answered with "dfdgfdsgfd" is not a
        record of a decision, it is a toll in front of a button. What actually stops a bad send is
        unchanged and sits elsewhere — two kill-switch tiers that must both be open, §64 approval
        per recipient, and §30's preflight refusing anything malformed.
      */}
    </div>
  )
}

function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.post('/campaigns', { name }),
    onSuccess: async () => {
      setName('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      onClose()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : 'Could not create'),
  })

  return (
    <Dialog
      open={open}
      title="New campaign"
      description="It starts in dry run. Taking it out is a separate, recorded act."
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={name.trim().length < 2 || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <Field label="Name" error={error} hint="What this programme is for — visible only to your team.">
        {(props) => (
          <Input
            {...props}
            value={name}
            placeholder="IJSPE spring call for papers"
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
    </Dialog>
  )
}

/**
 * §64's review queue, and the two controls that had no console at all until now.
 *
 * The endpoints landed on 2026-08-22 and nothing called them (G023's known gaps), which left the
 * product in the state that gate found by hand: contacts imported, campaigns created, and no way
 * to join the two without a database client. This is the join, plus the human review that §64 puts
 * between it and a send.
 *
 * The order of the controls is the order of the work — add, review, plan — because that is what an
 * operator asked to "build a campaign" actually does, and a screen that offers them in any other
 * order teaches the wrong sequence.
 */
function Recipients({ campaign }: { campaign: CampaignDetail }) {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [approval, setApproval] = useState<ApprovalStatus | 'ALL'>('PENDING')
  const [selected, setSelected] = useState<string[]>([])
  const [adding, setAdding] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  /** The recipient whose letter is open. §64's preview — see `RecipientPreviewDrawer`. */
  const [previewing, setPreviewing] = useState<string | null>(null)
  /** The confirmation for rejecting a whole filtered set — the largest act this screen offers. */
  const [rejectingAll, setRejectingAll] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const recipients = useQuery({
    queryKey: ['campaigns', campaign.id, 'contacts', approval],
    queryFn: () =>
      api.get<CampaignRecipientPage>(
        `/campaigns/${campaign.id}/contacts?take=200` +
          (approval === 'ALL' ? '' : `&approval=${approval}`),
      ),
  })

  const refresh = async () => {
    setSelected([])
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id, 'contacts'] }),
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id, 'readiness'] }),
      queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    ])
  }

  /**
   * §64 across the whole filtered set, with the count pinned.
   *
   * The count sent is `recipients.data.total` — **what this screen rendered**, never a fresh read.
   * A refetch here to make the numbers agree would turn the server's 409 into a rubber stamp, which
   * is the failure the check exists to prevent.
   */
  const reviewAll = useMutation({
    mutationFn: ({ decision, reason }: { decision: 'approve' | 'reject'; reason?: string }) =>
      api.post<ReviewResult>(`/campaigns/${campaign.id}/contacts/${decision}-all`, {
        approval: approval === 'ALL' ? 'PENDING' : approval,
        expectedCount: recipients.data?.total ?? 0,
        reason,
      }),
    onSuccess: async (result) => {
      setRejectingAll(false)
      setError(null)
      setOutcome(
        [result.warning, `${result.decided} recipients decided.`]
          .filter(Boolean)
          .join(' '),
      )
      await refresh()
    },
    onError: (e: unknown) => {
      setRejectingAll(false)
      // The 409 is the interesting one and its message carries both numbers, so it is shown
      // verbatim rather than replaced with something friendlier and less useful.
      setError(e instanceof ApiError ? e.message : 'Nothing was decided.')
    },
  })

  const review = useMutation({
    mutationFn: ({ decision, reason }: { decision: 'approve' | 'reject'; reason?: string }) =>
      api.post<ReviewResult>(`/campaigns/${campaign.id}/contacts/${decision}`, {
        campaignContactIds: selected,
        reason,
      }),
    onSuccess: async (result) => {
      setRejecting(false)
      setError(null)
      // The warning first when there is one: recipients taken out of the dispatcher's queue is
      // the only outcome here that describes mail which would otherwise have gone out.
      setOutcome(
        [
          result.warning,
          `${result.decided.toLocaleString()} decided` +
            (result.alreadyDecided > 0 ? `, ${result.alreadyDecided} already were` : '') +
            (result.reversals > 0 ? `, ${result.reversals} reversed an earlier decision` : ''),
          result.nextStep,
        ]
          .filter(Boolean)
          .join(' · '),
      )
      await refresh()
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The decision was not applied'),
  })

  /**
   * §22's planner, for one campaign.
   *
   * Separate from approval on purpose, and the wording says why: approving decides *whether* a
   * person is contacted and planning decides *when*. `POST /campaigns/:id/contacts` and the review
   * endpoints all leave `nextActionAt` null, so this is the only control on the screen that puts
   * anything into the queue.
   */
  const plan = useMutation({
    mutationFn: () => api.post<PlanResult>(`/sending/campaigns/${campaign.id}/plan`, {}),
    onSuccess: async (result) => {
      setOutcome(
        `Planned: ${result.scheduled.toLocaleString()} of ${result.considered.toLocaleString()} considered scheduled` +
          (result.suppressed > 0 ? `, ${result.suppressed} suppressed` : '') +
          (result.skipped.length > 0
            ? ` — skipped: ${result.skipped.map((s) => `${s.count} ${s.reason}`).join('; ')}`
            : '') +
          (result.firstActionAt
            ? `. First send at ${new Date(result.firstActionAt).toLocaleString()}.`
            : '.'),
      )
      await refresh()
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The campaign could not be planned'),
  })

  const rows = recipients.data?.items ?? []
  const counts = recipients.data?.counts
  const mayReview = can('approve_messages')
  const mayAdd = can('manage_campaigns')
  const mayPlan = can('operate_dispatch')

  const TABS: Array<{ id: ApprovalStatus | 'ALL'; label: string }> = [
    { id: 'PENDING', label: 'Awaiting review' },
    { id: 'APPROVED', label: 'Approved' },
    { id: 'REJECTED', label: 'Rejected' },
    { id: 'ALL', label: 'Everyone' },
  ]

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-text m-0 text-[13px] font-semibold">Recipients</h4>
        {mayAdd && (
          <Button size="sm" icon="plus" onClick={() => setAdding(true)}>
            Add recipients
          </Button>
        )}
        {mayPlan && (
          <Button
            size="sm"
            onClick={() => {
              setError(null)
              plan.mutate()
            }}
            disabled={plan.isPending}
            // WCAG 2.5.3: an accessible name that does not contain the visible label makes the
            // control unspeakable — "click Plan now" then matches nothing. The visible words come
            // first and the explanation follows them.
            aria-label="Plan now — materialize send times for approved recipients"
          >
            {plan.isPending ? 'Planning…' : 'Plan now'}
          </Button>
        )}
      </div>

      {/* Not a tab strip from the scaffold: that one belongs to a ListScreen toolbar and this is
          inside a drawer. Same vocabulary, one level quieter. */}
      <div className="mb-2.5 flex flex-wrap gap-1.5">
        {TABS.map((tab) => {
          const count =
            tab.id === 'ALL'
              ? counts
                ? counts.PENDING + counts.APPROVED + counts.REJECTED + counts.NOT_REQUIRED
                : undefined
              : counts?.[tab.id]

          return (
            <button
              key={tab.id}
              onClick={() => {
                setApproval(tab.id)
                setSelected([])
              }}
              className={cn(
                'rounded-[6px] border px-2 py-[3px] text-[12px] transition-colors',
                tab.id === approval
                  ? 'border-accent-line bg-accent-soft text-accent'
                  : 'border-line text-muted hover:text-text',
              )}
            >
              {tab.label}
              {count !== undefined && (
                <span className="ml-1.5 tabular-nums opacity-70">{count.toLocaleString()}</span>
              )}
            </button>
          )
        })}
      </div>

      {campaign.requiresApproval ? null : (
        <p className="text-faint mb-2 text-[12px]">
          Human review is turned off for this campaign, so recipients are added already cleared to
          send (§64). Nothing here is waiting on a person.
        </p>
      )}

      {outcome && (
        <div className="mb-2.5">
          <Notice tone="info" onDismiss={() => setOutcome(null)}>
            {outcome}
          </Notice>
        </div>
      )}
      {error && (
        <div className="mb-2.5">
          <Notice tone="bad" onDismiss={() => setError(null)}>
            {error}
          </Notice>
        </div>
      )}

      {recipients.isLoading ? (
        <p className="text-faint text-[12.5px]">Loading recipients…</p>
      ) : rows.length === 0 ? (
        <p className="text-muted text-[12.5px]">
          {approval === 'PENDING'
            ? 'Nothing is waiting on a decision.'
            : approval === 'ALL'
              ? 'Nobody is in this campaign yet. Adding contacts does not schedule them — they wait on review first (§64).'
              : 'No recipient is in that state.'}
        </p>
      ) : (
        <>
          {/* Deciding beyond the page. Offered only when the filtered set is genuinely larger
              than what is rendered — below that, selecting the rows is the same act with the
              reviewer having actually seen them, which §64 prefers. */}
          {mayReview &&
            approval !== 'ALL' &&
            recipients.data &&
            recipients.data.total > rows.length && (
              <div className="border-line mb-2 flex flex-wrap items-center gap-2 rounded-[7px] border px-3 py-2">
                <span className="text-muted text-[12.5px]">
                  {rows.length.toLocaleString()} shown of{' '}
                  <strong className="text-text">
                    {recipients.data.total.toLocaleString()}
                  </strong>{' '}
                  {APPROVAL_LABEL[approval].toLowerCase()}.
                </span>
                <Button
                  size="sm"
                  disabled={reviewAll.isPending}
                  onClick={() => reviewAll.mutate({ decision: 'approve' })}
                >
                  Approve all {recipients.data.total.toLocaleString()}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={reviewAll.isPending}
                  onClick={() => setRejectingAll(true)}
                >
                  Reject all
                </Button>
                <span className="text-faint text-[11.5px]">
                  Refused if the number has changed since this loaded.
                </span>
              </div>
            )}

          {mayReview && selected.length > 0 && (
            <div className="border-line mb-2 overflow-hidden rounded-[7px] border">
              <BulkBar count={selected.length} onClear={() => setSelected([])}>
                <BulkAction onClick={() => review.mutate({ decision: 'approve' })}>
                  Approve
                </BulkAction>
                <BulkAction destructive onClick={() => setRejecting(true)}>
                  Reject
                </BulkAction>
              </BulkBar>
            </div>
          )}

          <div className="border-line max-h-[320px] overflow-y-auto rounded-[7px] border">
            <Table>
              <THead>
                <Th width={34}>
                  {mayReview && (
                    <input
                      type="checkbox"
                      aria-label="Select every recipient shown"
                      checked={selected.length === rows.length && rows.length > 0}
                      onChange={(event) =>
                        setSelected(event.target.checked ? rows.map((row) => row.id) : [])
                      }
                    />
                  )}
                </Th>
                <Th>Address</Th>
                <Th width={120}>Review</Th>
                <Th width={110}>State</Th>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <Tr
                    key={row.id}
                    selected={selected.includes(row.id)}
                    onOpen={() => setPreviewing(row.id)}
                  >
                    <Td>
                      {mayReview && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.contact.canonicalEmail}`}
                          checked={selected.includes(row.id)}
                          onChange={(event) =>
                            setSelected((current) =>
                              event.target.checked
                                ? [...current, row.id]
                                : current.filter((id) => id !== row.id),
                            )
                          }
                        />
                      )}
                    </Td>
                    <Td mono>{row.contact.canonicalEmail}</Td>
                    <Td>
                      <Chip tone={APPROVAL_TONE[row.approvalStatus]}>
                        {APPROVAL_LABEL[row.approvalStatus]}
                      </Chip>
                    </Td>
                    <Td className="text-muted">
                      {row.status === 'SCHEDULED' && row.nextActionAt
                        ? new Date(row.nextActionAt).toLocaleString()
                        : RECIPIENT_STATE_LABEL[row.status]}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </div>

          {recipients.data && recipients.data.total > rows.length && (
            <p className="text-faint mt-1.5 text-[12px]">
              Showing {rows.length.toLocaleString()} of {recipients.data.total.toLocaleString()}.
              Selecting them all here selects the ones shown, not the rest.
            </p>
          )}
        </>
      )}

      <RecipientPreviewDrawer
        campaignId={campaign.id}
        campaignContactId={previewing}
        onClose={() => setPreviewing(null)}
        mayReview={mayReview}
        onApprove={(id) => {
          setSelected([id])
          review.mutate({ decision: 'approve' })
          setPreviewing(null)
        }}
        onReject={(id) => {
          setSelected([id])
          setPreviewing(null)
          setRejecting(true)
        }}
      />

      <ConfirmDialog
        open={rejectingAll}
        title="Reject every recipient in this filter?"
        body={
          <>
            This rejects{' '}
            <strong>{(recipients.data?.total ?? 0).toLocaleString()}</strong> recipients — every one
            matching <strong>{APPROVAL_LABEL[approval === 'ALL' ? 'PENDING' : approval]}</strong>,
            including the ones not on screen. Any already scheduled are taken out of the
            dispatcher's queue.
          </>
        }
        confirmLabel="Reject them all"
        destructive
        requireReason
        reasonLabel="Why are these being rejected?"
        pending={reviewAll.isPending}
        onCancel={() => setRejectingAll(false)}
        onConfirm={(reason) => reviewAll.mutate({ decision: 'reject', reason })}
      />

      <AddRecipientsDialog
        campaignId={campaign.id}
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={async (result) => {
          setOutcome(
            `${result.added.toLocaleString()} added` +
              (result.alreadyPresent > 0 ? `, ${result.alreadyPresent} already there` : '') +
              (result.unknown.length > 0
                ? `, ${result.unknown.length} unknown and skipped`
                : '') +
              `. ${result.nextStep}`,
          )
          setApproval('PENDING')
          await refresh()
        }}
      />

      <ConfirmDialog
        open={rejecting}
        title={`Reject ${selected.length === 1 ? 'this recipient' : `these ${selected.length} recipients`}?`}
        body="They will not be contacted by this campaign. Anyone already scheduled is taken out of the queue."
        confirmLabel="Reject"
        requireReason
        reasonLabel="Why are these being rejected?"
        reasonPlaceholder="outside the journal's subject scope"
        pending={review.isPending}
        error={error}
        onCancel={() => {
          setRejecting(false)
          setError(null)
        }}
        onConfirm={(reason) => review.mutate({ decision: 'reject', reason })}
      />
    </section>
  )
}

/**
 * The contact picker.
 *
 * Ids rather than a filter, because the API takes ids for a reason it states: a filter evaluated
 * server-side would add a different set from the one on screen. So the dialog can only add what it
 * has actually rendered, and it says so when the search matched more than a page.
 */
function AddRecipientsDialog({
  campaignId,
  open,
  onClose,
  onAdded,
}: {
  campaignId: string
  open: boolean
  onClose: () => void
  onAdded: (result: AddContactsResult) => void | Promise<void>
}) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250)
    return () => clearTimeout(timer)
  }, [search])

  // Reset on open rather than on close, the same rule ConfirmDialog uses: a selection left behind
  // would be added to whichever campaign the dialog was opened over next.
  useEffect(() => {
    if (open) {
      setChosen([])
      setSearch('')
      setDebounced('')
      setError(null)
    }
  }, [open])

  const contacts = useQuery({
    queryKey: ['contacts', 'picker', debounced],
    queryFn: () =>
      api.get<ContactPage>(
        '/contacts?take=100' + (debounced ? `&search=${encodeURIComponent(debounced)}` : ''),
      ),
    enabled: open,
  })

  const add = useMutation({
    mutationFn: () =>
      api.post<AddContactsResult>(`/campaigns/${campaignId}/contacts`, {
        contactIds: chosen,
      }),
    onSuccess: async (result) => {
      await onAdded(result)
      onClose()
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The recipients were not added'),
  })

  const items = contacts.data?.items ?? []

  return (
    <Dialog
      open={open}
      title="Add recipients"
      description="Adding does not schedule anyone. They wait on review, and then on the planner."
      width={560}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={chosen.length === 0 || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending
              ? 'Adding…'
              : chosen.length === 0
                ? 'Add'
                : `Add ${chosen.length.toLocaleString()}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Find contacts" hint="Search by address, name or institution.">
          {(props) => (
            <Input
              {...props}
              value={search}
              placeholder="waseda.jp"
              onChange={(event) => setSearch(event.target.value)}
            />
          )}
        </Field>

        {contacts.isLoading ? (
          <p className="text-faint text-[12.5px]">Loading contacts…</p>
        ) : items.length === 0 ? (
          <p className="text-muted text-[12.5px]">
            {debounced
              ? `No contact matches “${debounced}”.`
              : 'There are no contacts yet. Import some first.'}
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[12px]">
              <button
                className="text-accent hover:underline"
                onClick={() =>
                  setChosen(
                    chosen.length === items.length ? [] : items.map((contact) => contact.id),
                  )
                }
              >
                {chosen.length === items.length ? 'Clear all' : `Select all ${items.length}`}
              </button>
              {contacts.data && contacts.data.total > items.length && (
                <span className="text-faint">
                  {items.length} of {contacts.data.total.toLocaleString()} shown — narrow the
                  search to reach the rest
                </span>
              )}
            </div>

            <div className="border-line max-h-[280px] space-y-1 overflow-y-auto rounded-[7px] border p-2">
              {items.map((contact) => (
                <label
                  key={contact.id}
                  className="hover:bg-hover flex cursor-pointer items-center gap-2 rounded-[5px] px-1.5 py-1 text-[12.5px]"
                >
                  <input
                    type="checkbox"
                    checked={chosen.includes(contact.id)}
                    onChange={(event) =>
                      setChosen((current) =>
                        event.target.checked
                          ? [...current, contact.id]
                          : current.filter((id) => id !== contact.id),
                      )
                    }
                  />
                  <span className="font-mono text-[12px]">{contact.canonicalEmail}</span>
                  {(contact.firstName || contact.lastName) && (
                    <span className="text-muted truncate">
                      {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </>
        )}

        {error && (
          <p role="alert" className="text-bad text-[12.5px]">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  )
}
